import Routes from 'routes';
import { customFetch } from 'utils/fetch';

// PLA-1034/1035: costo DDP variable devuelto por el Pricing Simulator.
export interface DdpCost {
  id: number;
  ddp_cost_id: number;
  value: number;
  name: string;
  cost_type: string;
  cost_group: string;
  editable?: boolean;
  // PLA-1102: sólo en los editables. `min` es el valor configurado: un override menor da 400.
  default?: number;
  min?: number;
  step?: number;
}

export interface PricingPreviewParams {
  incoterm: string;
  qualityId?: string;
  units?: string;
  priceType?: string;
  shipmentDate?: string;
  shipperId?: string;
  opportunityId?: string;
  destinationWarehouseId?: string;
  // Lo que el usuario ingresó en el simulador, keyed por el `name` del campo en snake_case
  // (ej. { carry_months: '3', premium_ids: [50] }). El backend lo re-camelize para el API.
  pricingInputs?: Record<string, unknown>;
  ddpOverrides?: Record<string, string>;
  // El catálogo (fields/premiums/ddpCosts) solo cambia con incoterm/origen/bodega; cuando solo
  // se recalcula el precio se manda true para ahorrar los GET de opciones extra en el backend.
  skipCatalog?: boolean;
}

// PLA-1057: definición de un campo dinámico del simulador (array `fields`).
export interface SimulatorField {
  name: string;
  label?: LocalizedLabel;
  visible: boolean;
  required?: boolean;
  type: 'select' | 'multiselect' | 'number' | 'date' | 'object';
  optionsFrom?: string;
  values?: number[];
  default?: number | string;
  min?: number;
  step?: number;
  // PLA-1102: el API lo manda en `customMargin` junto con su `min`/`default`.
  allowed?: boolean;
}

// PLA-1057: prima del catálogo (countrySpecific). usdPerLb < 0 -> exige marca PEEBIES.
export interface Premium {
  id: number;
  value: number;
  label: string;
  category?: string;
  usdPerLb: number;
  countrySpecific?: boolean;
}

export interface CatalogOption {
  value: number | string;
  label: string;
}

// Etiqueta localizada que devuelve el Pricing Simulator ({ en, es, ... }).
export type LocalizedLabel = Record<string, string>;

export interface BreakdownItem {
  key: string;
  label: LocalizedLabel;
  usdPerLb: number;
  local?: { value: number; currency: string; unit: string };
  costType?: string;
  costGroup?: string;
}

export interface BreakdownSection {
  key: string;
  label: LocalizedLabel;
  items: BreakdownItem[];
  subtotalUsdPerLb?: number;
}

export interface PricingBreakdown {
  formula?: string;
  formula_with_values?: string;
  sections?: BreakdownSection[];
  total?: { label: LocalizedLabel; usdPerLb: number; formatted: string };
}

export interface PricingPreviewData {
  supported: boolean;
  fob_price: string | null;
  incoterm_price: string | null;
  ddp_costs: DdpCost[];
  breakdown?: PricingBreakdown | null;
  error?: string | null;
  reason?: string | null;
  // Catálogo (solo viene cuando no se manda skipCatalog).
  fields?: SimulatorField[];
  incoterm_fields?: SimulatorField[];
  premiums?: Premium[];
  contract_types?: CatalogOption[];
  boxes?: CatalogOption[];
}

export interface PricingPreviewResponse {
  success: boolean;
  data?: PricingPreviewData;
  errors?: string[];
}

export async function fetchPricingPreview(
  params: PricingPreviewParams
): Promise<PricingPreviewResponse> {
  const body = {
    incoterm: params.incoterm,
    quality_id: params.qualityId,
    units: params.units,
    price_type: params.priceType,
    shipment_date: params.shipmentDate,
    shipper_id: params.shipperId,
    opportunity_id: params.opportunityId,
    destination_warehouse_id: params.destinationWarehouseId,
    pricing_inputs: params.pricingInputs,
    pricing_ddp_overrides: params.ddpOverrides,
    skip_catalog: params.skipCatalog,
  };

  return customFetch<PricingPreviewResponse>(
    Routes.pricing_preview_api_v1_contracts_path(),
    body,
    'POST'
  );
}
